import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import useChannel from "hooks/use-channel";
import { createRequest } from "hooks/use-fetch";
import { postJson, putJson } from "utils/fetch";
import { transformErrors } from "utils/transform-errors";

import ValidationErrors from "screens/shared/validation-errors";

import {
  Button,
  Container,
  FormControl,
  FormHelperText,
  Link,
  OutlinedInput,
  Stack,
  Typography,
} from "components/core";
import NavigationBar from "components/navigation-bar";

const CheckCode = ({ channelKey, mobileNumber, restartFlow }) => {
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const codeRef = useRef(null);

  const check = useMemo(
    () => createRequest(putJson, setLoading, "leading"),
    [],
  );

  const login = useMemo(
    () => createRequest(postJson, setLoading, "leading"),
    [],
  );

  useEffect(() => {
    if (!channelKey || !mobileNumber) {
      restartFlow([], history);
    }
  }, [channelKey, mobileNumber, restartFlow, history]);

  const handleBackClick = () => {
    history.push("/");
  };

  const onReceived = useCallback(
    async (data) => {
      if (data.token) {
        const response = await login("/account/api/phone_logins/authenticate", {
          token: data.token,
          code: codeRef.current.value,
        });

        window.location = response.nextPath;
      } else if (data.retryable) {
        setLoading(false);
        setErrors([data.error]);
      } else {
        setLoading(false);
        restartFlow([data.error], history);
      }
    },
    [login, restartFlow, history],
  );

  const onDisconnected = useCallback(() => {
    setLoading(false);
  }, []);

  const [connected] = useChannel({
    channel: "Account::VerifyChannel",
    key: channelKey,
    onReceived,
    onDisconnected,
  });

  const handleCodeChange = (e) => setCode(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const response = await check("/account/api/phone_logins", {
      channelKey,
      mobileNumber,
      code,
    });

    if (response.errors) {
      setErrors(transformErrors(response.errors));
      setLoading(false);
    }
  };

  return (
    <Container disableGutters maxWidth="xs">
      <NavigationBar onBackClick={handleBackClick} />
      <Stack pb={[2, 3]} pt={[3, 4]} px={[2, 3]} spacing={4}>
        <Stack component="form" spacing={2} onSubmit={handleSubmit}>
          <ValidationErrors errors={errors} />
          <FormControl error={!!errors.code}>
            <Typography
              component="label"
              htmlFor="code-input"
              lineHeight="1.5"
              mb={2}
              variant="title"
            >
              Enter the code we’ve sent to {mobileNumber}
            </Typography>
            <OutlinedInput
              autoFocus
              autoComplete="one-time-code"
              id="code-input"
              inputProps={{
                "data-1p-ignore": true,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              inputRef={codeRef}
              placeholder="––––––"
              readOnly={loading}
              sx={{
                input: {
                  letterSpacing: 15,
                  fontSize: 18,
                  textAlign: "center",
                },
              }}
              value={code}
              onChange={handleCodeChange}
            />
            {errors.code && (
              <FormHelperText variant="standard">{errors.code}</FormHelperText>
            )}
          </FormControl>
          <Button loading={!connected || loading} type="submit">
            Log in
          </Button>
          <Typography>
            If you haven’t received a code, check you’ve typed your number
            correctly.{" "}
            <Link href="#" onClick={handleBackClick}>
              Or try using another method to log in.
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </Container>
  );
};

CheckCode.propTypes = {
  mobileNumber: PropTypes.string.isRequired,
  restartFlow: PropTypes.func.isRequired,
  channelKey: PropTypes.string,
};

export default CheckCode;
